<script lang="ts">
	import type { PartnerBoxMapContent, PartnerBoxmapTrackingData } from '$lib/components/advertising/partners/types';
	import { page } from '$app/state';
	import { AdvertisingPartnerType } from '$houdini';
	import PartnerBoxMap from '$lib/components/advertising/partners/boxmap/PartnerBoxMap.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { getSelectedAddress } from '$lib/components/map/selectedAddress.svelte';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { trackPartnerBoxmapClick, trackPartnerBoxmapLoaded } from '$lib/services/tracking/googleAnalytics/partners';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { setBookingUrlLabelPagename } from '$lib/utils/getBookingUrl';

	const { partnerBoxMapsAreReduced, partnerBoxMapsContainerExpandedWidth } = getMapLayoutContext();
	const searchParams = getSearchParamsStore();
	const REDUCE_AFTER_TIME = 10_000;

	const { itineraryResultsStore, boxmapsForItineraryStore, boxmapsForPageStore, pagename } = $derived(page.data);

	let boxmapsContainer: HTMLElement | undefined = $state();
	let computeContainerWidthTimer: number;
	let reduceTimer: number;

	function expandBoxMaps() {
		if (!$partnerBoxMapsAreReduced && boxmapsContainer) {
			$partnerBoxMapsContainerExpandedWidth = 'auto';

			computeContainerWidthTimer = window.setTimeout(() => {
				if (!boxmapsContainer) {
					return;
				}

				$partnerBoxMapsContainerExpandedWidth = `${boxmapsContainer.offsetWidth}px`;
			}, REDUCE_AFTER_TIME - 200);

			reduceTimer = window.setTimeout(() => {
				$partnerBoxMapsAreReduced = true;
			}, REDUCE_AFTER_TIME);
		}
	}

	function clearBoxMapsExpandedTimeouts() {
		clearTimeout(reduceTimer);
		clearTimeout(computeContainerWidthTimer);
	}

	const trackingData: PartnerBoxmapTrackingData = $derived.by(() => {
		const selectedAddress = getSelectedAddress();
		const routeIndex = $searchParams?.selectedRoute ?? 0;
		return {
			searchParams: $searchParams || {},
			routeIndex,
			route: $itineraryResultsStore?.routes[routeIndex] ?? undefined,
			arrivalAddress: $itineraryResultsStore?.arrivalAddress ?? (selectedAddress && {
				city: selectedAddress.city,
				country: selectedAddress.country ? { code: '', name: selectedAddress.country } : null,
				department: selectedAddress.department,
				region: selectedAddress.region,
				zipCode: selectedAddress.zipCode,
			}),
			departureAddress: $itineraryResultsStore?.departureAddress ?? undefined,
			waypoints: $itineraryResultsStore?.waypoints,
		};
	});

	const boxmaps = $derived.by(() => {
		// Check if both stores are available
		const hasPageStore = boxmapsForPageStore && ($boxmapsForPageStore?.length ?? 0) > 0;
		const hasItineraryStore = boxmapsForItineraryStore && ($boxmapsForItineraryStore?.length ?? 0) > 0;

		let nextBoxmaps: PartnerBoxMapContent[] = [];

		// If both are available, prioritize page store
		if (hasPageStore) {
			nextBoxmaps = $boxmapsForPageStore ?? [];
		} else if (hasItineraryStore) {
			nextBoxmaps = $boxmapsForItineraryStore ?? [];
		}

		// If neither is available, return undefined
		return nextBoxmaps.map(item => item.type === AdvertisingPartnerType.BOOKING
			? {
				...item,
				url: pagename ? setBookingUrlLabelPagename(item.url, pagename) : item.url,
			}
			: item);
	});

	$effect(() => {
		if (boxmaps?.length && boxmapsContainer) {
			expandBoxMaps();
		}

		return clearBoxMapsExpandedTimeouts;
	});

	const shouldReduced = $derived($isMobile && $partnerBoxMapsAreReduced);
</script>

{#if boxmaps.length > 0}
	<div
		bind:this={boxmapsContainer}
		class="flex flex-col gap-1 group"
		class:justify-end={$isMobile && $partnerBoxMapsAreReduced}
		class:items-end={$isMobile && $partnerBoxMapsAreReduced}
		style="--expanded-container-width: {($isMobile && $partnerBoxMapsContainerExpandedWidth) || 'auto'}"
	>
		{#each boxmaps as boxMap (boxMap.url)}
			{@const trackingDataWithAdvertiser = { ...trackingData, advertiser: boxMap.type }}
			{@const isBookingBoxmap = boxMap.type === 'BOOKING'}
			<PartnerBoxMap
				data={boxMap}
				shortMessage={(shouldReduced && isBookingBoxmap && boxMap.shortMessage) || undefined}
				class={`${shouldReduced ? `${(!isBookingBoxmap && 'w-[36px]') || ''} transition-width ease-in-out md:group-hover:w-[--expanded-container-width] overflow-hidden` : 'w-[--expanded-container-width]'}`}
				onclick={() => trackPartnerBoxmapClick(trackingDataWithAdvertiser)}
				onMount={() => trackPartnerBoxmapLoaded(trackingDataWithAdvertiser)}
			/>
		{/each}
	</div>
{/if}
